import type { DeliveryMethod } from "@au-schein-de/au-ui-library";
import type {
	AggregatedVendorCount,
	VendorListItem,
} from "@dransay/api/models";
import {
	DELIVERY_METHOD_KIND,
	VENDOR_AGGREGATION_GROUP,
} from "@dransay/api/models";
import type { DeliveryMethodKind } from "@dransay/db";
import uniqBy from "lodash/uniqBy";
import type { ReactNode } from "react";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useFetcher, useRouteLoaderData } from "react-router";
import { isCombinedPaymentEnabled } from "~/features/products/combined-payment.utils";
import {
	minEnabledFreeFromCents,
	type VendorOptionForPicker,
} from "~/features/products/pharmacy-picker/map-vendor-to-picker-item";
import { PharmacyPickerResponsiveVendorSelect } from "~/features/products/pharmacy-picker/pharmacy-picker-responsive-vendor-select.component";
import type { PharmacyPickerSortContext } from "~/features/products/pharmacy-picker/pharmacy-picker-sort-preference";
import { formatCentsAsAmount } from "~/hooks/use-currency-price.hook";
import { useDebouncedCallback } from "~/hooks/use-debounced-callback.hook";
import { useMarket } from "~/hooks/use-market";
import { usePostalCodeParam } from "~/hooks/use-postal-code-param";
import { captureEvent } from "~/tooling/telemetry/capture-event";
import {
	noResultsSearchedEvent,
	pharmacySearchedEvent,
} from "~/tooling/telemetry/events";
import { resolveVendorFeatured } from "~/utils/vendor";
import type { Route } from "../../../../routes/+types/_storefront";
import { getAggregatedVendorOptions } from "../../vendor-options";
import { formatAddress } from "../utils/format-address";
import { buildVendorsFilteredListInput } from "./build-vendors-filtered-list-input";
import { mapFilteredVendorToListItem } from "./map-filtered-vendor-to-list-item";
import { ResponsiveSelectVendor } from "./responsive-select-vendor.component";
import {
	buildPickerGroupShortcuts,
	buildPickerPharmacies,
	buildVendorsQueryParams,
	isVendorListLoading,
	resolveListRadiusKm,
	resolvePickerPlaceholder,
} from "./vendor-filter.utils";

type RawDeliveryMethod = {
	kind: string;
	enabled: boolean;
	freeFrom?: number | null;
};

const buildDeliveryMethodLabels = (
	methods: RawDeliveryMethod[],
	locale: string,
	formatFreeFromLabel: (amount: string) => string,
): Partial<Record<DeliveryMethod, string | undefined>> => {
	const enabled = methods.filter(
		(m) => m.enabled && m.kind !== "express-courier",
	);
	const labels: Partial<Record<DeliveryMethod, string | undefined>> = {};
	for (const kind of ["shipping", "courier"] as const) {
		const candidates = enabled
			.filter((m) => m.kind === kind)
			.map((m) => m.freeFrom)
			.filter((v): v is number => typeof v === "number" && v > 0);
		if (enabled.some((m) => m.kind === kind)) {
			labels[kind] =
				candidates.length > 0
					? formatFreeFromLabel(
							formatCentsAsAmount(Math.min(...candidates), locale),
						)
					: undefined;
		}
	}
	return labels;
};

export type VendorCoords = {
	id: number;
	lat: number;
	lng: number;
	name: string;
	distance: number;
	featured?: boolean;
};

export type MinimalVendorOption = {
	id: string | number | null | undefined;
	name?: string | null;
	deliveryMethods?: Array<{ kind: string; enabled: boolean }>;
	distance?: number;
	featured?: boolean;
};

export type VendorFilterProps = {
	value?: string;
	onChange: (vendorId?: string) => void;
	onDropdownOpenChange?: (isOpen: boolean) => void;
	deliveryMethodKind?: DeliveryMethodKind;
	defaultVendor?: VendorListItem;
	disabled?: boolean;
	hidePostalCodeInput?: boolean;
	mobileMapSlot?: ReactNode;
	variant?: "default" | "product" | "pharmacy";
	keepOpenOnSelect?: boolean;
	onVendorDataChange?: (vendors: VendorCoords[]) => void;
	additionalVendors?: MinimalVendorOption[];
	footerSlot?: ReactNode;
	onIncludeNonTopPharmaciesChange?: (includeNonTop: boolean) => void;
	radiusControl?: ReactNode;
	/** Express-courier filter state, owned by the surface that fetches the list. */
	expressSelected?: boolean;
	onExpressChange?: (selected: boolean) => void;
	/** Shown above the (empty) list when the express availability check failed. */
	expressCheckFailedMessage?: ReactNode;
	mobileRadiusControl?: ReactNode;
	/**
	 * Radius the picker list is capped to, in km, or `null` for "any radius".
	 * Supplied by VendorFilterMapWrapper so the list matches the radius control
	 * rendered in this picker's own filter row; without it the list would stay
	 * pinned to the legacy 10 km cap while the control offered up to 25 km,
	 * showing map markers whose pharmacies never appear in the list beside them.
	 */
	listRadiusKm?: number | null;
	/**
	 * When the map wrapper already owns POST /api/vendors-filtered, pass that
	 * page here so VendorFilter does not issue a second list request.
	 * Distances on items are still in meters (API shape); the filter maps them.
	 */
	externalVendorsFiltered?: {
		items: VendorListItem[];
		aggregatedCount?: AggregatedVendorCount;
		/** Radius-scoped counts for the bulk rows (Figma 4662:59239). */
		scopedAggregatedCount?: AggregatedVendorCount;
		isLoading?: boolean;
	};
};

export function VendorFilter({
	value,
	onChange,
	onDropdownOpenChange,
	deliveryMethodKind,
	defaultVendor,
	disabled,
	hidePostalCodeInput = false,
	mobileMapSlot,
	variant,
	keepOpenOnSelect,
	onVendorDataChange,
	additionalVendors = [],
	footerSlot,
	onIncludeNonTopPharmaciesChange,
	radiusControl,
	expressSelected,
	onExpressChange,
	expressCheckFailedMessage,
	mobileRadiusControl,
	listRadiusKm,
	externalVendorsFiltered,
}: VendorFilterProps) {
	const usesExternalVendorsFiltered = externalVendorsFiltered != null;
	const { t, i18n } = useTranslation(["product", "common"]);
	const [search, setSearch] = useState("");
	const [listIncludeNonTop, setListIncludeNonTop] = useState(
		() => value === VENDOR_AGGREGATION_GROUP.ALL,
	);
	// `value` holds either a group or a single pharmacy, so it cannot answer
	// whether the Top filter is on: picking a pharmacy out of the Top list left
	// the chip reading "off" while the list stayed narrowed to Top.
	const [topFilterSelected, setTopFilterSelected] = useState(
		() => value === VENDOR_AGGREGATION_GROUP.TOP,
	);
	const notifyMapShowAllMarkers = useCallback(
		(showAll: boolean) => {
			onIncludeNonTopPharmaciesChange?.(showAll);
		},
		[onIncludeNonTopPharmaciesChange],
	);
	const storefrontLoaderData =
		useRouteLoaderData<Route.ComponentProps["loaderData"]>(
			"routes/_storefront",
		);
	const enableTopPharmacyCartPromotion =
		storefrontLoaderData?.enableTopPharmacyCartPromotion ?? false;
	const enablePharmacyFilters =
		storefrontLoaderData?.enablePharmacyFilters ?? false;
	const usePharmacyPicker =
		enablePharmacyFilters || enableTopPharmacyCartPromotion;
	// biome-ignore lint/correctness/useExhaustiveDependencies: sync aggregation scope from URL value and delivery method changes
	useEffect(() => {
		if (!usePharmacyPicker) return;
		if (value === VENDOR_AGGREGATION_GROUP.ALL) {
			setListIncludeNonTop(true);
			setTopFilterSelected(false);
			notifyMapShowAllMarkers(true);
		} else if (value === VENDOR_AGGREGATION_GROUP.TOP) {
			setListIncludeNonTop(false);
			setTopFilterSelected(true);
			notifyMapShowAllMarkers(false);
		}
	}, [value, deliveryMethodKind, usePharmacyPicker, notifyMapShowAllMarkers]);
	const { postalCode, setPostalCode } = usePostalCodeParam();
	const fetcher = useFetcher<{
		items: VendorListItem[];
		aggregatedCount: AggregatedVendorCount;
	}>();
	const selectedVendorFetcher = useFetcher<{ items: VendorListItem[] }>();
	const postalCodeFetcher = useFetcher<{
		items: { postalCode: string; city: string }[];
	}>();
	const market = useMarket();

	const listAggregatedCount =
		externalVendorsFiltered?.aggregatedCount ?? fetcher.data?.aggregatedCount;
	const additionalOptions = getAggregatedVendorOptions(
		deliveryMethodKind,
		t,
		listAggregatedCount,
	);
	const nonFeaturedPharmacyCount = Math.max(
		(listAggregatedCount?.all ?? 0) - (listAggregatedCount?.top ?? 0),
		0,
	);

	const trackSearchDebounced = useDebouncedCallback(
		(query) => {
			captureEvent(pharmacySearchedEvent(query));
		},
		{ leading: false, delay: 1000 },
	);
	const debouncedSearch = useDebouncedCallback((query) => {
		setSearch(query);
		trackSearchDebounced(query);
	});

	const debouncedPostalCodeSearch = useDebouncedCallback((query) => {
		setPostalCode(query);
		captureEvent(pharmacySearchedEvent(query));
	});

	const isPharmacyVariant = variant === "pharmacy";

	const setDefaultVendorForDeliveryMethod = () => {
		if (value) return;

		const defaultOption = deliveryMethodKind
			? additionalOptions?.[0]
			: undefined;
		handleChange(defaultOption?.id?.toString());
	};

	// biome-ignore lint/correctness/useExhaustiveDependencies: intentionally set default only when deliveryMethodKind changes
	useEffect(setDefaultVendorForDeliveryMethod, [deliveryMethodKind]);

	const isAggregationGroup = useCallback(
		(vendorId: string) => {
			const staticIds = new Set<string>(
				Object.values(VENDOR_AGGREGATION_GROUP),
			);
			return (
				staticIds.has(vendorId) ||
				(additionalOptions?.some((option) => option.id === vendorId) ?? false)
			);
		},
		[additionalOptions],
	);

	const loadVendors = () => {
		if (usesExternalVendorsFiltered) {
			return;
		}
		if (enablePharmacyFilters) {
			fetcher.submit(
				buildVendorsFilteredListInput({
					deliveryMethodKind,
					// Always the full set, even with Top selected. Search filters this
					// page client-side, so a Top-scoped request made non-top pharmacies
					// unfindable by name -- with matches on screen, nothing even hinted
					// that more existed. The picker still shows Top only, narrowing the
					// loaded page itself, and expands the non-top matches while
					// searching.
					listIncludeNonTop: true,
					postalCode,
				}),
				{
					method: "POST",
					action: "/api/vendors-filtered",
					encType: "application/json",
				},
			);
			return;
		}
		const params = buildVendorsQueryParams({
			search,
			postalCode,
			deliveryMethodKind,
			usePharmacyPicker,
			listIncludeNonTop,
			value,
			isAggregationGroup,
		});
		fetcher.load(`/api/vendors?${params}`);
	};

	// biome-ignore lint/correctness/useExhaustiveDependencies: flag-on search filters the loaded page; highlighting a pharmacy must not refetch the list
	useEffect(loadVendors, [
		enablePharmacyFilters ? "" : search,
		deliveryMethodKind,
		enablePharmacyFilters ? "" : value,
		postalCode,
		// Flag-on the request no longer depends on it: switching Top/All narrows
		// the page already loaded instead of fetching a different one.
		enablePharmacyFilters ? "" : listIncludeNonTop,
		usePharmacyPicker,
		enablePharmacyFilters,
		usesExternalVendorsFiltered,
	]);

	const handleChange = (value?: string) => {
		onChange(value);
	};

	const defaultVendorArray = defaultVendor ? [defaultVendor] : [];
	const hasIndividualVendorSelected = !!value && !isAggregationGroup(value);
	const fetchedSelectedItems = (
		hasIndividualVendorSelected
			? (selectedVendorFetcher.data?.items ?? defaultVendorArray)
			: []
	).map((vendor) => ({ ...vendor, hidden: !!search }));

	const fetchedListItems = useMemo(() => {
		const rawItems =
			externalVendorsFiltered?.items ?? fetcher.data?.items ?? [];
		if (enablePharmacyFilters || usesExternalVendorsFiltered) {
			return rawItems.map(mapFilteredVendorToListItem);
		}
		return rawItems;
	}, [
		enablePharmacyFilters,
		usesExternalVendorsFiltered,
		externalVendorsFiltered?.items,
		fetcher.data?.items,
	]);
	const listContainsSelectedPharmacy =
		!!value && fetchedListItems.some((item) => String(item.id) === value);

	const loadSelectedVendor = () => {
		if (!value || isAggregationGroup(value)) return;
		if (
			(enablePharmacyFilters || usesExternalVendorsFiltered) &&
			listContainsSelectedPharmacy
		) {
			return;
		}
		const params = new URLSearchParams({ id: value });
		if (isPharmacyVariant && postalCode) {
			params.append("postalCode", postalCode);
		}
		selectedVendorFetcher.load(`/api/vendors?${params}`);
	};

	// biome-ignore lint/correctness/useExhaustiveDependencies: hydrate only when the selected pharmacy is missing from the loaded list
	useEffect(loadSelectedVendor, [
		value,
		postalCode,
		isPharmacyVariant,
		enablePharmacyFilters,
		listContainsSelectedPharmacy,
	]);

	const vendorOptions = useMemo(
		() =>
			uniqBy(
				[
					...(additionalOptions ?? []),
					...fetchedSelectedItems,
					...fetchedListItems,
					...additionalVendors,
				],
				"id",
			)
				.filter((vendor) => vendor.id != null)
				.map((vendor) => {
					const rawMethods =
						(vendor.deliveryMethods as RawDeliveryMethod[] | undefined) ?? [];
					return {
						...vendor,
						id: String(vendor.id),
						title: vendor.name ?? "",
						value: String(vendor.id),
						address:
							"address" in vendor && vendor.address
								? formatAddress(
										vendor.address as Parameters<typeof formatAddress>[0],
									)
								: undefined,
						deliveryMethods: Array.from(
							new Set(
								rawMethods
									.filter((d) => d.enabled && d.kind !== "express-courier")
									.map((d) => d.kind),
							),
						) as ("shipping" | "courier" | "pickup")[],
						deliveryMethodLabels: buildDeliveryMethodLabels(
							rawMethods,
							i18n.language,
							(amount) => t("common:delivery.freeFrom", { amount }),
						),
						showDeliveryMethodBadgesThreshold:
							market.showDeliveryMethodBadgesThreshold,
						featured: resolveVendorFeatured(vendor),
						combinedPaymentEnabled: isCombinedPaymentEnabled(
							"combinedPaymentEnabled" in vendor ? vendor : undefined,
						),
						freeFromCents: minEnabledFreeFromCents(rawMethods),
					};
				}),
		[
			additionalOptions,
			fetchedListItems,
			fetchedSelectedItems,
			market,
			additionalVendors,
			i18n.language,
			t,
		],
	);

	const isLoading = usesExternalVendorsFiltered
		? Boolean(externalVendorsFiltered?.isLoading)
		: isVendorListLoading(
				fetcher.state,
				!!fetcher.data,
				selectedVendorFetcher.state,
				!!selectedVendorFetcher.data,
			);

	useEffect(() => {
		if (!onVendorDataChange) return;
		const coords = vendorOptions.flatMap((v) => {
			const id = Number(v.id);
			const lat = (v as { lat?: unknown }).lat;
			const lng = (v as { lng?: unknown }).lng;
			if (
				!Number.isFinite(id) ||
				!Number.isFinite(lat) ||
				!Number.isFinite(lng)
			) {
				return [];
			}
			return [
				{
					id,
					lat: lat as number,
					lng: lng as number,
					name: v.name ?? "",
					distance: ((v as { distance?: unknown }).distance as number) ?? 0,
					featured: (v as { featured?: boolean }).featured ?? false,
				},
			];
		});
		onVendorDataChange(coords);
	}, [vendorOptions, onVendorDataChange]);

	useEffect(() => {
		const items = externalVendorsFiltered?.items ?? fetcher.data?.items;
		if (search && !items?.length) {
			captureEvent(noResultsSearchedEvent("pharmacy", search));
		}
	}, [fetcher.data?.items, externalVendorsFiltered?.items, search]);

	const handlePostalCodeInputChange = (search: string) => {
		if (search.trim()) {
			postalCodeFetcher.load(`/api/postal-codes-autocomplete?search=${search}`);
		}
	};

	const reset = () => {
		setSearch("");
	};

	const postalCodeProps = hidePostalCodeInput
		? {}
		: {
				onPostalCodeSearch: debouncedPostalCodeSearch,
				onPostalCodeInputChange: handlePostalCodeInputChange,
				postalCodeSuggestions: postalCodeFetcher.data?.items ?? [],
				noPostalCodeResultsText: t("vendor.select.postalCode.noResults"),
				searchByPostalCodeText: t("vendor.select.localSearch"),
				isPostalCodeLoading: postalCodeFetcher.state === "loading",
			};

	const isMapDelivery =
		deliveryMethodKind === "pickup" || deliveryMethodKind === "courier";
	const effectiveListRadiusKm = resolveListRadiusKm(listRadiusKm);
	const pickerPharmacies = useMemo(
		() =>
			buildPickerPharmacies({
				vendorOptions: vendorOptions as VendorOptionForPicker[],
				isMapDelivery,
				listRadiusKm: effectiveListRadiusKm,
				enablePharmacyFilters,
				search,
			}),
		[
			vendorOptions,
			isMapDelivery,
			enablePharmacyFilters,
			search,
			effectiveListRadiusKm,
		],
	);

	// Figma 4662:59239: the bulk rows count what the radius actually shows —
	// "6 of 460 in 10 km" makes the bulk selector a 6, not a 460.
	const scopedGroupCounts = externalVendorsFiltered?.scopedAggregatedCount;
	const pickerGroupShortcuts = useMemo(() => {
		const shortcuts = buildPickerGroupShortcuts(
			additionalOptions,
			t,
			deliveryMethodKind === DELIVERY_METHOD_KIND.SHIPPING,
		);
		if (!scopedGroupCounts) {
			return shortcuts;
		}
		return shortcuts.map((shortcut) => ({
			...shortcut,
			count:
				shortcut.id === VENDOR_AGGREGATION_GROUP.TOP
					? scopedGroupCounts.top
					: scopedGroupCounts.all,
		}));
	}, [additionalOptions, t, deliveryMethodKind, scopedGroupCounts]);

	const pickerPlaceholder = useMemo(
		() =>
			resolvePickerPlaceholder({
				value,
				vendorOptions,
				additionalOptions,
				isAggregationGroup,
			}),
		[value, vendorOptions, additionalOptions, isAggregationGroup],
	);

	const showPickerDistance = variant === "pharmacy" && isMapDelivery;
	// Figma "Sort by": the list view offers Entfernung only for pickup/courier
	// with an address, and never offers the PDP-only price sort.
	const sortContext: PharmacyPickerSortContext = useMemo(
		() => ({
			surface: "list",
			deliveryMethodKind,
			hasAddress: Boolean(postalCode),
		}),
		[deliveryMethodKind, postalCode],
	);

	if (usePharmacyPicker) {
		return (
			<PharmacyPickerResponsiveVendorSelect
				pharmacies={pickerPharmacies}
				searchQuery={search}
				onSearch={debouncedSearch}
				isLoading={isLoading}
				disabled={disabled}
				value={hasIndividualVendorSelected ? value : undefined}
				onChange={(next) => handleChange(next)}
				onClose={reset}
				onDropdownOpenChange={onDropdownOpenChange}
				showDistance={showPickerDistance}
				sortContext={sortContext}
				groupShortcuts={pickerGroupShortcuts}
				availableTotal={listAggregatedCount?.all}
				onSelectGroup={(groupId, options) => {
					const isAll = groupId === VENDOR_AGGREGATION_GROUP.ALL;
					setListIncludeNonTop(isAll);
					setTopFilterSelected(groupId === VENDOR_AGGREGATION_GROUP.TOP);
					notifyMapShowAllMarkers(isAll);
					// Figma 4643:88797: the Top/Pay chips route through here too, and
					// a chip toggle must not reset what the customer typed.
					if (!options?.keepSearch) {
						setSearch("");
					}
					handleChange(groupId);
				}}
				onExpandNonTop={() => setListIncludeNonTop(true)}
				activeGroupId={
					value === VENDOR_AGGREGATION_GROUP.TOP ||
					value === VENDOR_AGGREGATION_GROUP.ALL
						? value
						: undefined
				}
				topFilterSelected={topFilterSelected}
				nonFeaturedCount={nonFeaturedPharmacyCount}
				mobileMapSlot={mobileMapSlot}
				keepOpenOnSelect={keepOpenOnSelect}
				footerSlot={footerSlot}
				placeholder={pickerPlaceholder ?? undefined}
				enablePharmacyFilters={enablePharmacyFilters}
				deliveryMethodKind={deliveryMethodKind}
				radiusControl={radiusControl}
				mobileRadiusControl={mobileRadiusControl}
				expressSelected={expressSelected}
				onExpressChange={onExpressChange}
				expressCheckFailedMessage={expressCheckFailedMessage}
			/>
		);
	}

	return (
		<ResponsiveSelectVendor
			options={vendorOptions}
			onSearch={debouncedSearch}
			isLoading={isLoading}
			disabled={disabled}
			value={value}
			onChange={handleChange}
			onClose={reset}
			onDropdownOpenChange={onDropdownOpenChange}
			noResultsText={t("vendor.select.noResults")}
			noResultsDescriptionText={t("vendor.select.noResultsDescription")}
			searchPlaceholder={t("search.vendor.placeholder.withLocalSearch")}
			searchForVendorText={t("vendor.select.searchForVendor.withLocalSearch")}
			mobileMapSlot={mobileMapSlot}
			variant={variant ?? "pharmacy"}
			keepOpenOnSelect={keepOpenOnSelect}
			footerSlot={footerSlot}
			{...postalCodeProps}
		/>
	);
}
